import React, { useEffect, useState, useContext, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import az from "date-fns/locale/az";
import api from "../../services/api";
import { HeyatYoluContext } from "../../App";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import './styles/EditMemorialProfile.css'

registerLocale("az", az);

// ── Sabitlər ──────────────────────────────────────────────
const FILE_VALIDATION = {
  MAX_SIZE: 10 * 1024 * 1024,
  ALLOWED_IMAGES: ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"],
  ALLOWED_VIDEOS: ["video/mp4", "video/avi", "video/mov", "video/webm"],
  MAX_MEDIA_FILES: 10,
  MAX_BIO_LENGTH: 2000,
  get ALLOWED_MEDIA() {
    return [...this.ALLOWED_IMAGES, ...this.ALLOWED_VIDEOS];
  },
};

const CEREMONY_LABELS = {
  threeDay: "3-cü gün",
  sevenDay: "7-ci gün",
  fortyDay: "40-cı gün",
  anniversary: "İl dönümü",
};

const CEREMONY_DAY_LIMITS = {
  threeDay: 5,
  sevenDay: 10,
  fortyDay: 45,
  anniversary: 370,
};

const EMPTY_CEREMONY = { active: false, date: null, location: "", mapLink: "" };

/** Backend DateOnly formatı: YYYY-MM-DD */
const formatDateOnly = (date) => {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/** Fayl validasiyası */
const validateFile = (file, isProfilePhoto = false) => {
  if (!file) return "Fayl seçilməyib";
  if (file.size > FILE_VALIDATION.MAX_SIZE) return "Fayl ölçüsü 10MB-dan böyük ola bilməz";
  if (isProfilePhoto && !FILE_VALIDATION.ALLOWED_IMAGES.includes(file.type))
    return "Profil şəkli yalnız JPEG, PNG, GIF, WEBP formatında ola bilər";
  if (!isProfilePhoto && !FILE_VALIDATION.ALLOWED_MEDIA.includes(file.type))
    return "Yalnız şəkil və video faylları qəbul edilir";
  return null;
};

// ══════════════════════════════════════════════════════════
function EditMemorialProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { religions, relationTypes } = useContext(HeyatYoluContext);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    fatherName: "",
    lastName: "",
    dateOfBirth: null,
    dateOfDeath: null,
    religionId: "",
    birthPlace: "",
    deathPlace: "",
    biography: "",
  });

  const [photo, setPhoto] = useState(null);
  const [existingPhotoUrl, setExistingPhotoUrl] = useState("");
  const [mediaFiles, setMediaFiles] = useState([]);
  const [existingMedia, setExistingMedia] = useState([]);
  const [deletedMediaIds, setDeletedMediaIds] = useState([]);
  const [isMartyr, setIsMartyr] = useState(false);
  const [mourners, setMourners] = useState([]);
  const [showBioTip, setShowBioTip] = useState(false);

  const [wantsCeremonies, setWantsCeremonies] = useState(false);
  const [ceremonies, setCeremonies] = useState({
    threeDay: { ...EMPTY_CEREMONY },
    sevenDay: { ...EMPTY_CEREMONY },
    fortyDay: { ...EMPTY_CEREMONY },
    anniversary: { ...EMPTY_CEREMONY },
  });

  const mediaPreviewUrls = useRef(new Set());
  const photoPreviewUrl = useRef(null);

  // ── Profilin yüklənməsi ─────────────────────────────────
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await api.get(`/memorialprofile/${id}`);
        const p = res.data;

        setFormData({
          firstName: p.firstName || "",
          fatherName: p.fatherName || "",
          lastName: p.lastName || "",
          dateOfBirth: p.dateOfBirth ? new Date(p.dateOfBirth) : null,
          dateOfDeath: p.dateOfDeath ? new Date(p.dateOfDeath) : null,
          religionId: p.religionId || "",
          birthPlace: p.birthPlace || "",
          deathPlace: p.deathPlace || "",
          biography: p.biography || "",
        });

        setExistingPhotoUrl(p.photoUrl || "");
        setExistingMedia(p.media || []);
        setIsMartyr(p.isMartyr || false);
        setMourners(
          (p.mourners || []).map((m) => ({
            relationTypeId: m.relationTypeId || "",
            fullName: m.fullName || "",
          }))
        );

        const parseCeremony = (c) =>
          c
            ? { active: true, date: new Date(c.date), location: c.location || "", mapLink: c.mapLink || "" }
            : { ...EMPTY_CEREMONY };

        const loaded = {
          threeDay: parseCeremony(p.threeDay),
          sevenDay: parseCeremony(p.sevenDay),
          fortyDay: parseCeremony(p.fortyDay),
          anniversary: parseCeremony(p.anniversary),
        };

        setCeremonies(loaded);
        setWantsCeremonies(Object.values(loaded).some((c) => c.active));
      } catch (err) {
        console.error("Yükləmə xətası:", err);
        toast.error("Profil məlumatı yüklənmədi.");
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [id]);

  useEffect(() => {
    return () => {
      if (photoPreviewUrl.current) URL.revokeObjectURL(photoPreviewUrl.current);
      mediaPreviewUrls.current.forEach((url) => URL.revokeObjectURL(url));
      mediaPreviewUrls.current.clear();
    };
  }, []);

  const availableCeremonies = useMemo(() => {
    if (!formData.dateOfDeath) {
      return { threeDay: false, sevenDay: false, fortyDay: false, anniversary: false };
    }
    const diffDays = Math.floor((new Date() - new Date(formData.dateOfDeath)) / (1000 * 60 * 60 * 24));
    return Object.fromEntries(
      Object.entries(CEREMONY_DAY_LIMITS).map(([key, limit]) => [key, diffDays <= limit])
    );
  }, [formData.dateOfDeath]);

  // ── Handlers ────────────────────────────────────────────
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleDateChange = useCallback(
    (field) => (date) => setFormData((prev) => ({ ...prev, [field]: date })),
    []
  );

  const handlePhotoChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const error = validateFile(file, true);
    if (error) { toast.error(error); e.target.value = ""; return; }
    if (photoPreviewUrl.current) URL.revokeObjectURL(photoPreviewUrl.current);
    photoPreviewUrl.current = URL.createObjectURL(file);
    setPhoto(file);
  }, []);

  const handleMediaAdd = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (existingMedia.length + mediaFiles.length + files.length > FILE_VALIDATION.MAX_MEDIA_FILES) {
      toast.error(`Maksimum ${FILE_VALIDATION.MAX_MEDIA_FILES} media faylı yükləyə bilərsiniz`);
      e.target.value = ""; return;
    }
    const valid = [];
    for (const file of files) {
      const err = validateFile(file, false);
      if (err) { toast.error(`${file.name}: ${err}`); }
      else {
        const url = URL.createObjectURL(file);
        file._previewUrl = url;
        mediaPreviewUrls.current.add(url);
        valid.push(file);
      }
    }
    if (valid.length) setMediaFiles((prev) => [...prev, ...valid]);
    e.target.value = "";
  }, [existingMedia.length, mediaFiles.length]);

  const handleNewMediaRemove = useCallback((index) => {
    setMediaFiles((prev) => {
      const f = prev[index];
      if (f?._previewUrl) { URL.revokeObjectURL(f._previewUrl); mediaPreviewUrls.current.delete(f._previewUrl); }
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleExistingMediaRemove = useCallback((index) => {
    setExistingMedia((prev) => {
      const removed = prev[index];
      if (removed?.id) setDeletedMediaIds((ids) => [...ids, removed.id]);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleMournerChange = useCallback((index, field, value) => {
    setMourners((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  }, []);

  const addMournerField = useCallback(() => {
    setMourners((prev) => [...prev, { relationTypeId: "", fullName: "" }]);
  }, []);

  const removeMournerField = useCallback((index) => {
    setMourners((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleCeremonyChange = useCallback((key, field, value) => {
    setCeremonies((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  }, []);

  const activateCeremony = useCallback((key) => {
    setCeremonies((prev) => ({ ...prev, [key]: { ...prev[key], active: true } }));
  }, []);

  const deactivateCeremony = useCallback((key) => {
    setCeremonies((prev) => ({ ...prev, [key]: { ...EMPTY_CEREMONY } }));
  }, []);

  // ── Submit ──────────────────────────────────────────────
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (saving) return;

    if (!formData.firstName || !formData.lastName || !formData.fatherName) {
      toast.error("Ad, soyad və ata adı sahələri mütləqdir"); return;
    }
    if (!formData.dateOfBirth || !formData.dateOfDeath) {
      toast.error("Doğum və vəfat tarixləri mütləqdir"); return;
    }
    if (new Date(formData.dateOfBirth) > new Date(formData.dateOfDeath)) {
      toast.error("Doğum tarixi vəfat tarixindən sonra ola bilməz"); return;
    }
    if (!formData.religionId) { toast.error("Din seçimi mütləqdir"); return; }
    if (!formData.birthPlace || !formData.deathPlace) {
      toast.error("Doğum yeri və dəfn yeri mütləqdir"); return;
    }

    setSaving(true);

    try {
      const data = new FormData();

      data.append("FirstName", formData.firstName);
      data.append("FatherName", formData.fatherName);
      data.append("LastName", formData.lastName);
      data.append("DateOfBirth", formatDateOnly(formData.dateOfBirth));
      data.append("DateOfDeath", formatDateOnly(formData.dateOfDeath));
      data.append("BirthPlace", formData.birthPlace);
      data.append("DeathPlace", formData.deathPlace);
      data.append("Biography", formData.biography || "");
      data.append("ReligionId", formData.religionId);

      if (photo) data.append("Photo", photo);

      mediaFiles.forEach((file) => data.append("MediaFiles", file));

      if (deletedMediaIds.length > 0) {
        data.append("DeletedMediaIds", deletedMediaIds.join(","));
      }

      mourners.forEach((m, i) => {
        data.append(`Mourners[${i}].RelationTypeId`, m.relationTypeId);
        data.append(`Mourners[${i}].FullName`, m.fullName);
      });

      Object.entries(ceremonies).forEach(([key, ceremony]) => {
        if (ceremony.active && ceremony.date) {
          const capitalKey = key.charAt(0).toUpperCase() + key.slice(1);
          data.append(`${capitalKey}.Date`, formatDateOnly(ceremony.date));
          data.append(`${capitalKey}.Location`, ceremony.location || "");
          data.append(`${capitalKey}.MapLink`, ceremony.mapLink || "");
        }
      });

      await api.put(`/memorialprofile/${id}`, data);
      toast.success("Profil uğurla yeniləndi.");
      navigate("/kabinetim");
    } catch (err) {
      console.error("Redaktə xətası:", err);
      console.error("Response data:", err.response?.data);
      const msg = err.response?.data?.message || err.response?.data;
      toast.error(typeof msg === "string" ? msg : "Profil redaktə edilə bilmədi.");
    } finally {
      setSaving(false);
    }
  }, [saving, formData, photo, mediaFiles, deletedMediaIds, mourners, ceremonies, id, navigate]);

  // ── Loading ─────────────────────────────────────────────
  if (loading) {
    return (
      <div className="emp-loading">
        <div className="emp-loading__spinner" />
        <p className="emp-loading__text">Profil yüklənir...</p>
      </div>
    );
  }

  const photoPreviewSrc = photo
    ? photoPreviewUrl.current || URL.createObjectURL(photo)
    : existingPhotoUrl
    ? `https://api.heyatyolu.az/${existingPhotoUrl}`
    : null;

  // ── Render ──────────────────────────────────────────────
  return (
    <div className="emp">
      <Helmet>
        <title>Profili Redaktə Et – HeyatYolu</title>
        <meta name="description" content="Yaradılmış xatirə profilini redaktə edin." />
        <meta name="keywords" content="xatirə redaktəsi, profil düzəlişi, mərhum, şəhid, bioqrafiya, heyatyolu" />
        <meta property="og:title" content="Profili Redaktə Et – HeyatYolu" />
        <meta property="og:description" content="Əzizinizə aid yaradılmış xatirə profilini yeniləyin." />
        <meta property="og:type" content="website" />
      </Helmet>

      <header className="emp__header">
        <h2 className="emp__title">✏️ Profili Redaktə Et</h2>
      </header>

      <form className="emp__form" onSubmit={handleSubmit} encType="multipart/form-data">

        {/* ── Əsas Məlumatlar ── */}
        <div className="emp__section">
          <h3 className="emp__section-title">Əsas Məlumatlar</h3>
          <div className="emp__grid">
            <div className="emp__field">
              <label className="emp__label">Ad <span className="emp__required">*</span></label>
              <input className="emp__input" type="text" name="firstName" value={formData.firstName} onChange={handleChange} maxLength={50} required />
            </div>
            <div className="emp__field">
              <label className="emp__label">Ata adı <span className="emp__required">*</span></label>
              <input className="emp__input" type="text" name="fatherName" value={formData.fatherName} onChange={handleChange} maxLength={50} required />
            </div>
            <div className="emp__field">
              <label className="emp__label">Soyad <span className="emp__required">*</span></label>
              <input className="emp__input" type="text" name="lastName" value={formData.lastName} onChange={handleChange} maxLength={50} required />
            </div>
            <div className="emp__field">
              <label className="emp__label">Din <span className="emp__required">*</span></label>
              <select className="emp__select" name="religionId" value={formData.religionId} onChange={handleChange} required>
                <option value="">-- Seçin --</option>
                {religions.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* ── Tarixlər ── */}
        <div className="emp__section">
          <h3 className="emp__section-title">Tarixlər</h3>
          <div className="emp__grid emp__grid--2">
            <div className="emp__field">
              <label className="emp__label">Doğum tarixi <span className="emp__required">*</span></label>
              <DatePicker
                selected={formData.dateOfBirth}
                onChange={handleDateChange("dateOfBirth")}
                dateFormat="dd-MM-yyyy"
                locale="az"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                maxDate={formData.dateOfDeath || new Date()}
                yearDropdownItemNumber={100}
                placeholderText="Tarixi seçin"
                className="emp__datepicker"
                required
              />
            </div>
            <div className="emp__field">
              <label className="emp__label">Vəfat tarixi <span className="emp__required">*</span></label>
              <DatePicker
                selected={formData.dateOfDeath}
                onChange={handleDateChange("dateOfDeath")}
                dateFormat="dd-MM-yyyy"
                locale="az"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                maxDate={new Date()}
                minDate={formData.dateOfBirth}
                yearDropdownItemNumber={100}
                placeholderText="Tarixi seçin"
                className="emp__datepicker"
                required
              />
            </div>
          </div>
        </div>

        {/* ── Məkanlar ── */}
        <div className="emp__section">
          <h3 className="emp__section-title">Məkanlar</h3>
          <div className="emp__grid emp__grid--2">
            <div className="emp__field">
              <label className="emp__label">Doğum yeri <span className="emp__required">*</span></label>
              <input className="emp__input" type="text" name="birthPlace" value={formData.birthPlace} onChange={handleChange} maxLength={100} required />
            </div>
            <div className="emp__field">
              <label className="emp__label">Dəfn yeri <span className="emp__required">*</span></label>
              <input className="emp__input" type="text" name="deathPlace" value={formData.deathPlace} onChange={handleChange} maxLength={100} required />
            </div>
          </div>
        </div>

        {/* ── Bioqrafiya ── */}
        <div className="emp__section">
          <h3 className="emp__section-title">
            Bioqrafiya
            <button type="button" className="emp__info-btn" onClick={() => setShowBioTip((p) => !p)}>ℹ️</button>
          </h3>
          {showBioTip && (
            <div className="emp__info-panel">
              <p>Təhsil, peşə, xidmət və ictimai fəaliyyəti haqqında məlumat yazın.</p>
            </div>
          )}
          <textarea
            className="emp__textarea"
            name="biography"
            value={formData.biography}
            onChange={handleChange}
            maxLength={FILE_VALIDATION.MAX_BIO_LENGTH}
            rows={5}
            placeholder="Bioqrafiya məlumatlarını daxil edin..."
          />
          <span className="emp__char-count">
            {formData.biography.length}/{FILE_VALIDATION.MAX_BIO_LENGTH}
          </span>
        </div>

        {/* ── Yas tutanlar ── */}
        <div className="emp__section">
          <h3 className="emp__section-title">Yas tutanlar</h3>
          <div className="emp__mourners">
            {mourners.map((mourner, index) => (
              <div key={index} className="emp__mourner-row">
                <select
                  className="emp__select"
                  value={mourner.relationTypeId}
                  onChange={(e) => handleMournerChange(index, "relationTypeId", e.target.value)}
                  required
                >
                  <option value="">-- Əlaqə növü --</option>
                  {relationTypes.map((type) => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
                <input
                  className="emp__input"
                  type="text"
                  placeholder="Ad və soyad"
                  value={mourner.fullName}
                  onChange={(e) => handleMournerChange(index, "fullName", e.target.value)}
                  maxLength={100}
                  required
                />
                <button type="button" className="emp__mourner-remove" onClick={() => removeMournerField(index)}>✕</button>
              </div>
            ))}
          </div>
          <button type="button" className="emp__btn emp__btn--add" onClick={addMournerField}>
            ➕ Yas tutanı əlavə et
          </button>
        </div>

        {/* ── Mərasimlər ── */}
        {Object.values(availableCeremonies).some((v) => v) && (
          <div className="emp__section">
            <h3 className="emp__section-title">🕯️ Mərasim bölməsi</h3>

            <label className="emp__checkbox-label">
              <input
                type="checkbox"
                className="emp__checkbox"
                checked={wantsCeremonies}
                onChange={(e) => setWantsCeremonies(e.target.checked)}
              />
              Mərasim tarixlərini qeyd etmək istəyirsiniz?
            </label>

            {wantsCeremonies && (
              <div className="emp__ceremonies">
                <div className="emp__ceremony-actions">
                  {Object.entries(availableCeremonies).map(
                    ([key, isAvailable]) =>
                      isAvailable && !ceremonies[key].active && (
                        <button key={key} type="button" className="emp__btn emp__btn--ceremony-add" onClick={() => activateCeremony(key)}>
                          ➕ {CEREMONY_LABELS[key]} mərasimi əlavə et
                        </button>
                      )
                  )}
                </div>

                {Object.entries(ceremonies).map(
                  ([key, value]) =>
                    value.active && (
                      <div key={key} className="emp__ceremony-card">
                        <h4 className="emp__ceremony-title">{CEREMONY_LABELS[key]} mərasimi</h4>
                        <div className="emp__grid emp__grid--2">
                          <div className="emp__field">
                            <label className="emp__label">Tarix</label>
                            <DatePicker
                              selected={value.date}
                              onChange={(date) => handleCeremonyChange(key, "date", date)}
                              dateFormat="dd-MM-yyyy"
                              locale="az"
                              placeholderText="Tarixi seçin"
                              className="emp__datepicker"
                            />
                          </div>
                          <div className="emp__field">
                            <label className="emp__label">Məkan</label>
                            <input
                              className="emp__input"
                              type="text"
                              value={value.location}
                              onChange={(e) => handleCeremonyChange(key, "location", e.target.value)}
                              placeholder="Məsələn: Yas mərasim evi"
                              maxLength={100}
                            />
                          </div>
                        </div>
                        <div className="emp__field emp__field--full">
                          <label className="emp__label">Xəritə linki</label>
                          <input
                            className="emp__input"
                            type="url"
                            value={value.mapLink}
                            onChange={(e) => handleCeremonyChange(key, "mapLink", e.target.value)}
                            placeholder="https://maps.google.com/..."
                          />
                        </div>
                        <button type="button" className="emp__btn emp__btn--ceremony-remove" onClick={() => deactivateCeremony(key)}>
                          ✖ Mərasimi sil
                        </button>
                      </div>
                    )
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Profil Şəkli ── */}
        <div className="emp__section">
          <h3 className="emp__section-title">📷 Profil Şəkli</h3>
          <div className="emp__photo">
            {photoPreviewSrc ? (
              <div className="emp__photo-preview">
                <img src={photoPreviewSrc} alt="Profil şəkli" className="emp__photo-img" />
              </div>
            ) : (
              <div className="emp__photo-empty">Profil şəkli yoxdur</div>
            )}
            <label className="emp__file-label">
              <span className="emp__file-label-text">📷 Yeni şəkil seç</span>
              <input
                type="file"
                accept={FILE_VALIDATION.ALLOWED_IMAGES.join(",")}
                onChange={handlePhotoChange}
                className="emp__file-input"
              />
            </label>
          </div>
        </div>

        {/* ── Mövcud Media ── */}
        {existingMedia.length > 0 && (
          <div className="emp__section">
            <h3 className="emp__section-title">Mövcud media</h3>
            <div className="emp__media-grid">
              {existingMedia.map((item, i) => (
                <div key={item.id || i} className="emp__media-item">
                  {item.fileType === "image" ? (
                    <img src={`https://api.heyatyolu.az/${item.url}`} alt="media" className="emp__media-content" />
                  ) : (
                    <video src={`https://api.heyatyolu.az/${item.url}`} controls className="emp__media-content" />
                  )}
                  <button type="button" className="emp__media-remove" onClick={() => handleExistingMediaRemove(i)}>×</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Yeni Media ── */}
        <div className="emp__section">
          <h3 className="emp__section-title">📁 Yeni Media Faylları</h3>
          <label className="emp__media-upload">
            <span className="emp__media-upload-icon">📁</span>
            <span className="emp__media-upload-text">Əlavə şəkil/video seçin</span>
            <span className="emp__media-upload-hint">(İstəyə uyğun boş buraxa bilərsiz)</span>
            <input
              type="file"
              multiple
              accept={FILE_VALIDATION.ALLOWED_MEDIA.join(",")}
              onChange={handleMediaAdd}
              className="emp__file-input"
            />
          </label>

          {mediaFiles.length > 0 && (
            <div className="emp__media-grid">
              {mediaFiles.map((file, index) => {
                const isImage = file.type.startsWith("image");
                const previewUrl = file._previewUrl || URL.createObjectURL(file);
                return (
                  <div key={index} className="emp__media-item">
                    {isImage ? (
                      <img src={previewUrl} alt={`media-${index}`} className="emp__media-content" />
                    ) : (
                      <video src={previewUrl} controls className="emp__media-content" />
                    )}
                    <button type="button" className="emp__media-remove" onClick={() => handleNewMediaRemove(index)}>×</button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Şəhid ── */}
        {isMartyr && (
          <div className="emp__martyr-badge">
            ✅ Bu şəxs şəhid kimi qeyd edilib. Bu status dəyişdirilə bilməz.
          </div>
        )}

        {/* ── Submit ── */}
        <div className="emp__actions">
          <button type="submit" className="emp__btn emp__btn--submit" disabled={saving}>
            {saving ? (
              <>
                <span className="emp__spinner" />
                Yadda saxlanılır...
              </>
            ) : (
              "💾 Yadda saxla"
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditMemorialProfile;
